import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { useLayoutEffect, useRef, type JSX } from 'react';

import { ensureCodeQualityEnabled } from '@/features/code-quality/guard.js';
import {
  parseProjectSearch,
  type ProjectSearch,
} from '@/features/code-quality/project-search.js';
import { ProjectPage } from '@/features/code-quality/ProjectPage.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { useRouteWorkspaceSync } from '@/features/workspace/useRouteWorkspaceSync.js';

/**
 * The report of one Code Quality project, in the workspace whose Digital Developer it scores.
 * `?run=<id>` opens that run's dashboard, so a link, such as the one in a Jira issue, can point at a
 * run; without it the page opens the project's last scored run. Selecting no run drops the parameter.
 * `?from=dashboard` marks a report opened from the Code Quality overview in Dashboards, so its way
 * back leads there; choosing another run keeps it.
 */
export const Route = createFileRoute('/workspaces/$id/code-quality/$projectId')(
  {
    validateSearch: parseProjectSearch,
    beforeLoad: async () => {
      await ensureCodeQualityEnabled();
    },
    component: CodeQualityProjectRoute,
  },
);

/**
 * Landing selects the workspace of the address in the sidebar, except on a report opened from the
 * Dashboards overview, which keeps the sidebar's workspace so the overview's node stays in sight. A
 * later pick in the switcher opens the Code Quality page, which shows the picked workspace, as a new
 * history entry, because this project belongs to the one left behind. The page always gets the
 * workspace of the address, never the pick, so a switch never reads as a project shown in the wrong
 * workspace.
 */
function CodeQualityProjectRoute(): JSX.Element {
  const { id, projectId } = useParams({
    from: '/workspaces/$id/code-quality/$projectId',
  });
  const { run, from } = useSearch({
    from: '/workspaces/$id/code-quality/$projectId',
  });
  const navigate = useNavigate();
  const openCodeQualityPage = (): void =>
    void navigate({ to: '/code-quality' });
  /** Opens the project in `workspaceId` with the run `runId`, keeping the origin, in place. */
  function replaceWith(workspaceId: string, runId: string | undefined): void {
    const search: ProjectSearch = {
      ...(runId === undefined ? {} : { run: runId }),
      ...(from === undefined ? {} : { from }),
    };
    void navigate({
      to: '/workspaces/$id/code-quality/$projectId',
      params: { id: workspaceId, projectId },
      search,
      replace: true,
    });
  }
  return (
    <>
      {from === 'dashboard' ? (
        <FollowUserSwitch onUserSwitch={openCodeQualityPage} />
      ) : (
        <SelectRouteWorkspace
          workspaceId={id}
          onUserSwitch={openCodeQualityPage}
        />
      )}
      <ProjectPage
        workspaceId={id}
        projectId={projectId}
        selectedRunId={run ?? null}
        from={from}
        onSelectRun={(runId) => replaceWith(id, runId ?? undefined)}
        onWrongWorkspace={(projectWorkspaceId) =>
          replaceWith(projectWorkspaceId, run)
        }
      />
    </>
  );
}

/** Selects the workspace of the address in the sidebar, and hands a later pick to `onUserSwitch`. */
function SelectRouteWorkspace({
  workspaceId,
  onUserSwitch,
}: {
  workspaceId: string;
  onUserSwitch: () => void;
}): null {
  useRouteWorkspaceSync(workspaceId, onUserSwitch);
  return null;
}

/** Leaves the sidebar's workspace as it is, and hands a pick in the switcher to `onUserSwitch`. */
function FollowUserSwitch({
  onUserSwitch,
}: {
  onUserSwitch: () => void;
}): null {
  useUserWorkspaceSwitch(onUserSwitch);
  return null;
}

/**
 * Calls `onUserSwitch` when the person picks another workspace in the sidebar switcher. A change the
 * app makes itself, such as loading the stored selection, is ignored.
 */
function useUserWorkspaceSwitch(onUserSwitch: () => void): void {
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  const hasHydrated = useUiStore((state) => state.hasHydrated);
  const previousRef = useRef<string | null | undefined>(undefined);
  const onUserSwitchRef = useRef(onUserSwitch);
  useLayoutEffect(() => {
    onUserSwitchRef.current = onUserSwitch;
  });
  useLayoutEffect(() => {
    if (!hasHydrated) return;
    const previous = previousRef.current;
    previousRef.current = selectedWorkspaceId;
    if (previous === undefined || previous === selectedWorkspaceId) return;
    if (useUiStore.getState().lastWorkspaceChangeReason === 'user') {
      onUserSwitchRef.current();
    }
  }, [selectedWorkspaceId, hasHydrated]);
}
